import { AbstractConnector } from '@web3-react/abstract-connector'
import { ConnectorUpdate } from '@web3-react/types'
import { EventEmitter } from 'events'

declare global {
  interface Window {
    __WC_LOADED__?: boolean
    __WC_PROVIDER__?: any
    EthereumProvider?: any
    WalletConnect?: any
    WalletConnectProvider?: any
  }
}

/**
 * WalletConnect v2 Connector for web3-react v6
 * Uses WalletConnect v2 EthereumProvider imported from npm
 */
export class WalletConnectV2Connector extends AbstractConnector {
  private provider: any
  private chainId: number
  private projectId: string
  private rpcUrl: string
  private emitter: EventEmitter
  private wcCore: any

  constructor(chainId: number, projectId: string, rpcUrl: string) {
    super({ supportedChainIds: [chainId] })
    this.chainId = chainId
    this.projectId = projectId
    this.rpcUrl = rpcUrl
    this.emitter = new EventEmitter()
  }

  async activate(): Promise<ConnectorUpdate> {
    console.log('%c[WalletConnectV2Connector] activate() called', 'color: orange; font-weight: bold')
    
    if (!this.provider) {
      try {
        console.log('Initializing WalletConnect...')
        const result = await this._initializeWalletConnect()
        console.log('WalletConnect initialized successfully, setting provider')
        this.provider = result.provider
        this.wcCore = result.wcCore

        console.log('Returning activation result:', { account: result.account, chainId: result.chainId })
        return { provider: this.provider, account: result.account, chainId: result.chainId }
      } catch (error) {
        console.error('%c[WalletConnectV2Connector] Activation failed', 'color: red; font-weight: bold')
        console.error('Error message:', error instanceof Error ? error.message : String(error))
        console.error('Full error:', error)
        throw error
      }
    }

    try {
      const accounts = await this.provider.request({
        method: 'eth_accounts',
      })

      if (!accounts || !accounts.length) {
        throw new Error('No accounts available')
      }

      const chainIdHex = await this.provider.request({
        method: 'eth_chainId',
      })
      const connectedChainId = parseInt(chainIdHex, 16)

      return { provider: this.provider, account: accounts[0], chainId: connectedChainId }
    } catch (error) {
      throw error
    }
  }

  private async _initializeWalletConnect(): Promise<{
    provider: any
    wcCore: any
    account: string
    chainId: number
  }> {
    console.log('%c[_initializeWalletConnect] Starting with dynamic WalletConnect load', 'color: purple; font-weight: bold')
    
    try {
      // Load WalletConnect dynamically from CDN
      const wcModule = await this._loadWalletConnectDynamically()
      
      console.log('[WC] WalletConnect module loaded:', { hasInit: !!wcModule.init, hasEthereumProvider: !!wcModule.EthereumProvider, hasDefault: !!wcModule.default, isEsModule: wcModule.__esModule })
      console.log('[WC] Initializing with config:', { projectId: this.projectId, chainId: this.chainId })
      
      // The library is exported as a module with an EthereumProvider.init() method
      let wcProvider: any
      
      try {
        // Check if it's WalletConnect v2 EthereumProvider
        if (wcModule.EthereumProvider && typeof wcModule.EthereumProvider.init === 'function') {
          console.log('[WC] Using WalletConnect v2 EthereumProvider.init()')
          console.log('[WC] Available constants:', {
            REQUIRED_EVENTS: wcModule.REQUIRED_EVENTS,
            REQUIRED_METHODS: wcModule.REQUIRED_METHODS,
            OPTIONAL_EVENTS: wcModule.OPTIONAL_EVENTS,
            OPTIONAL_METHODS: wcModule.OPTIONAL_METHODS
          })
          
          // Clear any corrupted WalletConnect data
          console.log('[WC] Clearing any existing WalletConnect localStorage data...')
          try {
            const keysToRemove = []
            for (let i = 0; i < localStorage.length; i++) {
              const key = localStorage.key(i)
              if (key && (key.includes('walletconnect') || key.includes('wc@'))) {
                keysToRemove.push(key)
              }
            }
            keysToRemove.forEach(key => {
              console.log(`[WC] Removing localStorage key: ${key}`)
              localStorage.removeItem(key)
            })
            console.log(`[WC] Cleared ${keysToRemove.length} WalletConnect localStorage entries`)
          } catch (e) {
            console.warn('[WC] Failed to clear localStorage:', e)
          }
          
          wcProvider = await wcModule.EthereumProvider.init({
            projectId: this.projectId,
            chains: [this.chainId],
            showQrModal: true,
            qrModalOptions: {
              themeMode: 'dark',
              themeVariables: {
                '--wcm-z-index': '1000',
                '--wcm-background-color': '#1a1a1a',
                '--wcm-accent-color': '#ff007a',
                '--wcm-text-color': '#ffffff',
                '--wcm-input-background-color': '#2a2a2a',
                '--wcm-input-border-color': '#3a3a3a',
                '--wcm-button-text-color': '#ffffff',
                '--wcm-button-background-color': '#ff007a',
                '--wcm-button-hover-background-color': '#e6006d',
              },
              enableExplorer: false, // This should hide the wallet explorer completely
              explorerRecommendedWalletIds: [], // Empty array
            },
            methods: wcModule.REQUIRED_METHODS,
            optionalMethods: wcModule.OPTIONAL_METHODS,
            events: wcModule.REQUIRED_EVENTS,
            optionalEvents: wcModule.OPTIONAL_EVENTS,
            rpcMap: {
              [this.chainId]: this.rpcUrl,
            },
            metadata: {
              name: 'ETCMC DEX',
              description: 'ETC Mainnet Decentralized Exchange',
              url: typeof window !== 'undefined' ? window.location.origin : '',
              icons: [typeof window !== 'undefined' ? (window.location.origin + '/favicon.ico') : ''],
            },
          })
          
          console.log('✓ Provider initialized with minimal config')
        } else if (wcModule.__esModule && wcModule.default) {
          console.log('[WC] Using ES module default export (WalletConnect v1)')
          console.log('[WC] Default export:', wcModule.default)
          console.log('[WC] Default export type:', typeof wcModule.default)
          
          try {
            const WalletConnectProviderConstructor = wcModule.default
            console.log('[WC] Creating provider with config:', {
              infuraId: null,
              rpc: { [this.chainId]: this.rpcUrl }
            })
            wcProvider = new WalletConnectProviderConstructor({
              infuraId: null, // Not needed for ETC
              rpc: {
                [this.chainId]: this.rpcUrl,
              },
            })
            console.log('[WC] Provider created successfully:', !!wcProvider)
          } catch (constructorErr) {
            console.error('[WC] Constructor failed:', constructorErr)
            throw constructorErr
          }
        } else if (typeof wcModule.init === 'function') {
          console.log('[WC] Using module.init()')
          wcProvider = await wcModule.init({
            projectId: this.projectId,
            chains: [this.chainId],
            showQrModal: true,
            rpcMap: {
              [this.chainId]: this.rpcUrl,
            },
            metadata: {
              name: 'ETCMC DEX',
              description: 'ETC Mainnet Decentralized Exchange',
              url: typeof window !== 'undefined' ? window.location.origin : '',
              icons: [typeof window !== 'undefined' ? (window.location.origin + '/favicon.ico') : ''],
            },
          })
        } else if (typeof wcModule === 'function') {
          console.log('[WC] Using module as constructor')
          wcProvider = new wcModule({
            projectId: this.projectId,
            chains: [this.chainId],
            showQrModal: true,
            rpcMap: {
              [this.chainId]: this.rpcUrl,
            },
            metadata: {
              name: 'ETCMC DEX',
              description: 'ETC Mainnet Decentralized Exchange',
              url: typeof window !== 'undefined' ? window.location.origin : '',
              icons: [typeof window !== 'undefined' ? (window.location.origin + '/favicon.ico') : ''],
            },
          })
        } else {
          console.error('[WC] Cannot determine initialization method. Module:', wcModule)
          throw new Error('Cannot determine WalletConnect initialization method')
        }
      } catch (initErr) {
        console.error('[WC ERROR] Initialization failed:', initErr)
        throw initErr
      }
      
      console.log('✓ Provider initialized')
      
      // For WalletConnect v2, we need to call connect() to establish session
      // even if it shows as already connected
      console.log('[WC] Calling connect() to establish session...')
      try {
        const session = await wcProvider.connect()
        console.log('✓ Session established:', session)
      } catch (connectErr) {
        console.error('[WC ERROR] Connect failed:', connectErr)
        throw new Error('WalletConnect session failed: ' + ((connectErr as any).message || String(connectErr)))
      }
      
      // Now request accounts
      console.log('[WC] Requesting accounts...')
      const accounts = await wcProvider.request({ method: 'eth_requestAccounts' })
      console.log('✓ Got accounts:', accounts)
      
      if (!accounts || !accounts.length) {
        throw new Error('No accounts returned')
      }

      const account = accounts[0]

      // Get final chain ID after potential switch
      let finalChainIdHex: string
      try {
        finalChainIdHex = await wcProvider.request({ method: 'eth_chainId' })
        console.log('✓ Final chain ID after switch:', finalChainIdHex)
      } catch (err) {
        console.error('[WC ERROR] Get final chain ID failed:', err)
        throw new Error('Failed to get final chain ID')
      }

      const finalConnectedChainId = parseInt(finalChainIdHex, 16)
      console.log('Final connected chain ID:', finalConnectedChainId, 'Target chain ID:', this.chainId)

      if (finalConnectedChainId !== this.chainId) {
        console.log('[WC] Requesting network switch from', finalConnectedChainId, 'to', this.chainId)
        try {
          // Try to switch to the correct chain
          await wcProvider.request({
            method: 'wallet_switchEthereumChain',
            params: [{ chainId: '0x' + this.chainId.toString(16) }],
          })
          console.log('✓ Network switched to', this.chainId)
        } catch (switchErr) {
          // If chain is not recognized, try to add it
          if ((switchErr as any).code === 4902) {
            console.log('[WC] Chain not in wallet, adding it...')
            try {
              await wcProvider.request({
                method: 'wallet_addEthereumChain',
                params: [
                  {
                    chainId: '0x' + this.chainId.toString(16),
                    chainName: 'ETC Mainnet',
                    nativeCurrency: {
                      name: 'Ether',
                      symbol: 'ETC',
                      decimals: 18,
                    },
                    rpcUrls: [this.rpcUrl],
                    blockExplorerUrls: ['https://blockscout.com/etc/mainnet/'],
                  },
                ],
              })
              console.log('✓ Chain added and switched')
            } catch (addErr) {
              console.error('[WC ERROR] Failed to add chain:', addErr)
              throw new Error('Failed to add ETC Mainnet to wallet')
            }
          } else {
            console.error('[WC ERROR] Failed to switch network:', switchErr)
            throw new Error('User rejected network switch')
          }
        }
      }

      // Setup listeners
      this._setupListeners(wcProvider)

      // Wrap provider
      const wrappedProvider = {
        request: (args: any) => wcProvider.request(args),
        send: (args: any, callback: any) => {
          wcProvider.request(args).then((r: any) => callback(null, r)).catch((e: any) => callback(e))
        },
        sendAsync: (args: any, callback: any) => {
          wcProvider.request(args).then((r: any) => callback(null, r)).catch((e: any) => callback(e))
        },
        on: (event: string, callback: any) => {
          if (wcProvider.on) wcProvider.on(event, callback)
        },
        removeListener: (event: string, callback: any) => {
          if (wcProvider.removeListener) wcProvider.removeListener(event, callback)
        },
      }

      return { provider: wrappedProvider, wcCore: wcProvider, account, chainId: this.chainId }
    } catch (error) {
      console.error('[_initializeWalletConnect] Error:', error)
      throw error
    }
  }

  private _setupListeners(wcProvider: any): void {
    if (!wcProvider || typeof wcProvider.on !== 'function') {
      return
    }

    wcProvider.on('disconnect', () => {
      this._cleanup()
      this.emitter.emit('disconnect')
    })

    wcProvider.on('accountsChanged', (accounts: string[]) => {
      if (accounts && accounts.length > 0) {
        this.emitter.emit('accountsChanged', accounts)
      } else {
        this._cleanup()
      }
    })

    wcProvider.on('chainChanged', (chainId: string | number) => {
      this.emitter.emit('chainChanged', chainId)
    })
  }

  async getProvider(): Promise<any> {
    return this.provider
  }

  async getChainId(): Promise<number> {
    if (!this.provider) {
      throw new Error('Provider not initialized')
    }

    const chainIdHex = await this.provider.request({
      method: 'eth_chainId',
    })
    return parseInt(chainIdHex, 16)
  }

  async getAccount(): Promise<string> {
    if (!this.provider) {
      throw new Error('Provider not initialized')
    }

    const accounts = await this.provider.request({
      method: 'eth_accounts',
    })
    if (!accounts || accounts.length === 0) {
      throw new Error('No accounts available')
    }
    return accounts[0]
  }

  deactivate(): void {
    this._cleanup()
    this.emitDeactivate()
  }

  private _cleanup(): void {
    if (this.wcCore && typeof this.wcCore.disconnect === 'function') {
      try {
        this.wcCore.disconnect()
      } catch (error) {
        console.warn('Error disconnecting WalletConnect:', error)
      }
    }
    this.provider = null
    this.wcCore = null
    this.emitter.removeAllListeners()
  }

  getEmitter(): EventEmitter {
    return this.emitter
  }

  isAvailable(): boolean {
    return this.provider !== null
  }

  get walletConnectProvider(): any {
    return this.wcCore
  }

  private async _loadWalletConnectDynamically(): Promise<any> {
    console.log('[_loadWalletConnectDynamically] Looking for WalletConnect provider...')
    
    // Wait a moment for scripts to initialize
    await new Promise(resolve => setTimeout(resolve, 1000))
    
    // Check for the loaded WalletConnect provider (this version exposes to window.WalletConnect)
    if (window.WalletConnect) {
      console.log('[_loadWalletConnectDynamically] ✓ Found window.WalletConnect')
      return window.WalletConnect
    }
    
    // Check for EthereumProvider directly
    if (window.EthereumProvider) {
      console.log('[_loadWalletConnectDynamically] ✓ Found window.EthereumProvider')
      return { EthereumProvider: window.EthereumProvider }
    }
    
    // Check for WalletConnectProvider directly (v1 CDN)
    if (window.WalletConnectProvider) {
      console.log('[_loadWalletConnectDynamically] ✓ Found window.WalletConnectProvider')
      return window.WalletConnectProvider
    }
    
    // Check for the provider by its package name
    if ((window as any)['@walletconnect/ethereum-provider']) {
      console.log('[_loadWalletConnectDynamically] ✓ Found @walletconnect/ethereum-provider')
      const wcModule = (window as any)['@walletconnect/ethereum-provider']
      console.log('[_loadWalletConnectDynamically] Module contents:', wcModule)
      console.log('[_loadWalletConnectDynamically] Module keys:', Object.keys(wcModule))
      console.log('[_loadWalletConnectDynamically] Module type:', typeof wcModule)
      return wcModule
    }
    
    // Check for any wallet-related objects
    const walletKeys = Object.keys(window).filter(key => 
      key.toLowerCase().includes('wallet') || 
      key.toLowerCase().includes('connect') || 
      key.toLowerCase().includes('ethereum')
    )
    
    console.log('[_loadWalletConnectDynamically] Wallet keys found:', walletKeys)
    
    for (const key of walletKeys) {
      const obj = (window as any)[key]
      if (obj && typeof obj === 'object') {
        if (obj.EthereumProvider) {
          console.log(`[_loadWalletConnectDynamically] ✓ Found EthereumProvider in window.${key}`)
          return { EthereumProvider: obj.EthereumProvider }
        }
        if (obj.init && typeof obj.init === 'function') {
          console.log(`[_loadWalletConnectDynamically] ✓ Found provider object in window.${key}`)
          return obj
        }
      }
    }
    
    throw new Error('WalletConnect provider not found on window object')
  }
}
